import { Divider, Flex, IconButton, Text } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { useRecoilValue } from "recoil"
import { categoryLevels, pages, searchNavigationParams } from "../../../../../common/constants/constants"
import { breadcrumbs } from "../../../../../common/constants/search-constants"
import { useQueryParam } from "../../../../../common/lib/QueryParamContext"
import { useUrlParams } from "../../../../../common/lib/UrlParamsContext"
import { searchServiceLocaleState } from "../../../../../common/store/settingsStore"
import { FhResponseData } from "../../../../../common/types/search"
import {
  getDrilledCategories,
  getParentCategoryId,
  isRootCategoryBreadCrumb,
} from "../../../../../common/utils/search-utils"
import BackButton from "../../../../shared/BackButton"
import { CClose } from "../../../../shared/Icons"

type Props = {
  categories: FhResponseData
  breadcrumbsData: FhResponseData
  isOverlayView?: boolean
  closeOverlay?: () => void
}

const CategoryBreadcrumbs = ({ categories, breadcrumbsData, isOverlayView, closeOverlay }: Props) => {
  const router = useRouter()
  const { setUrlParams } = useUrlParams()
  const { queryParam } = useQueryParam()
  const searchServiceLocale = useRecoilValue(searchServiceLocaleState)

  const isCategoryNavigation =
    queryParam.name === searchNavigationParams.SYSTEMS || queryParam.name === searchNavigationParams.SERIES
  const drilledCategoriesBreadcrumbs = getDrilledCategories(breadcrumbsData)

  //Filter out the lower level categories because we don't render a category breadcrumb for them
  //The current categories selection will contain a category breadcrumb only if it's a lower level categories selection
  const upperLevelCategoriesBreadcrumbs = drilledCategoriesBreadcrumbs.filter(
    (drilledCategory: FhResponseData) =>
      !categories.some((category: FhResponseData) => `{${category.value.value}}` === drilledCategory.name.non_ml)
  )

  //Guard clauses before any computations
  if (upperLevelCategoriesBreadcrumbs.length === 0) return null
  if (upperLevelCategoriesBreadcrumbs.length === 1 && isCategoryNavigation) return null

  //Get the first category breadcrumb removal link from another breadcrumb in the response based on navigation
  //because the FH is broken and does not remove the whole category tree
  const firstCrumbRemoveLink = breadcrumbsData.find(
    ({ name }) =>
      name.type === breadcrumbs.SEARCH ||
      name.attribute_type === breadcrumbs.CHARACTERISTICS ||
      name.attribute_type === breadcrumbs.APPLICATION_PURPOSES ||
      (name.attribute_type === `${breadcrumbs.NEW}_${searchServiceLocale.toLowerCase()}` &&
        queryParam?.name === searchNavigationParams.NEW) ||
      (name.attribute_type === `${breadcrumbs.CROSS}_${searchServiceLocale.toLowerCase()}` &&
        queryParam?.name === searchNavigationParams.CROSS) ||
      (isRootCategoryBreadCrumb(name.non_ml) && queryParam?.name === searchNavigationParams.ALL_SPARE_PARTS) ||
      (isRootCategoryBreadCrumb(name.non_ml) && queryParam?.name === searchNavigationParams.MIX_AND_MATCH)
  )?.url_params

  const removeCategoryCrumb = (removeUrlParams: string, non_ml: string) => {
    /* Get the id of the ancestor category in the category tree */
    const parentCategoryId = getParentCategoryId(non_ml)

    /* Redirect to the system as tiles navigation only when returning to CH1 category on system lister page */
    if (isCategoryNavigation && parentCategoryId?.startsWith(categoryLevels.ONE)) {
      router.push(`/${pages.SYSTEMS}/${parentCategoryId}`)
    } else {
      /* Otherwise do a normal removal of the current category from the category drill-down filter */
      setUrlParams(removeUrlParams)
    }

    if (isOverlayView) closeOverlay()
  }

  return (
    <Flex
      mb={{ base: "6", md: "8" }}
      mt={isOverlayView ? "0" : "-2"}
      gap="6"
      rowGap={isOverlayView ? "2" : "3"}
      flexWrap="wrap"
      width="100%"
      flexDirection={isOverlayView ? "column" : "row"}
    >
      {upperLevelCategoriesBreadcrumbs.map((crumb: FhResponseData, index: number) => {
        /* Don't show the first breadcrumb for Systems and Series navigation */
        if (index === 0 && isCategoryNavigation) return null

        const removeUrlParams =
          index === 0 ? firstCrumbRemoveLink : upperLevelCategoriesBreadcrumbs[index - 1]?.url_params

        return isOverlayView ? (
          <BackButton
            key={crumb.name.non_ml}
            text={crumb.name.value}
            onClick={() => removeCategoryCrumb(removeUrlParams, crumb.name.non_ml)}
            aria-label={`Remove category ${crumb.name.value} from filter`}
          />
        ) : (
          <Flex align="center" key={crumb.name.non_ml}>
            <Text as="span" fontWeight="medium" fontSize="xl">
              {crumb.name.value}
            </Text>
            <IconButton
              variant="categoryBreadcrumb"
              minW="6"
              h="6"
              icon={<CClose w="2.5" h="2.5" stroke="currentColor" />}
              ml="2"
              isRound
              aria-label={`Remove category ${crumb.name.value} from filter`}
              onClick={() => removeCategoryCrumb(removeUrlParams, crumb.name.non_ml)}
            />
          </Flex>
        )
      })}
      {isOverlayView && <Divider mt="4" />}
    </Flex>
  )
}

export default CategoryBreadcrumbs
